import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faLock, faChevronRight, faComment } from '@fortawesome/free-solid-svg-icons';

import { dueLabel, slug } from '../../utils/taskHelpers';

/**
 * One task as a card in the chat's Tasks panel: title, when it is due,
 * priority and status chips, and a progress bar.
 *
 * Tasks have no progress field yet, so the bar is read off the status. Once a
 * real percentage exists it replaces PROGRESS here and nowhere else.
 *
 * A self-assigned task still waiting on a decision says so, and when the server
 * says this viewer may decide it (task.canApprove) the card offers Approve and
 * View. A div acting as a button, because a real one cannot hold those.
 *
 * Discussion this viewer has not read yet shows as a count beside the title,
 * like a chat's unread badge; reading the thread in the drawer clears it.
 */

export const PROGRESS = {
    Pending: 0,
    'In Progress': 50,
    'On Hold': 50,
    Completed: 100
};

// "Due 26 Oct, 6:00 PM" - overdueAt is the real instant, dueDate only the day.
const dueText = (t) => {
    if (t.status === 'Completed' && t.completedAt) {
        return `Completed ${new Date(t.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
    }
    const at = t.overdueAt || t.dueDate;
    if (!at) return '';
    const d = new Date(at);
    const day = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return t.overdueAt
        ? `Due ${day}, ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : `Due ${day}`;
};

const ChatTaskCard = ({ task, onOpen, onApprove, busy = false }) => {
    const due = task.dueDate
        ? dueLabel(task.dueDate, task.status === 'Completed', task.overdueAt, task.status === 'On Hold')
        : null;
    const progress = PROGRESS[task.status] ?? 0;

    const awaiting = task.isSelfAssigned && task.approvalStatus === 'Pending';
    // A masked private task has nothing to open.
    const open = task.isMasked ? undefined : () => onOpen(task);

    return (
        <div
            className={`msgr-tcard ${task.isMasked ? 'is-disabled' : ''} ${awaiting ? 'is-awaiting' : ''}`}
            role="button"
            tabIndex={task.isMasked ? -1 : 0}
            aria-disabled={task.isMasked || undefined}
            onClick={open}
            onKeyDown={(e) => {
                if (open && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    open();
                }
            }}
        >
            <span className="msgr-tcard-title">
                {task.isMasked && <FontAwesomeIcon icon={faLock} />}
                <span className="msgr-tcard-name">{task.title}</span>
                {task.unreadComments > 0 && (
                    <span
                        className="msgr-tcard-unread"
                        title={`${task.unreadComments} unread message${task.unreadComments === 1 ? '' : 's'}`}
                        aria-label={`${task.unreadComments} unread discussion message${task.unreadComments === 1 ? '' : 's'}`}
                    >
                        <FontAwesomeIcon icon={faComment} />
                        {task.unreadComments > 99 ? '99+' : task.unreadComments}
                    </span>
                )}
            </span>

            {dueText(task) && (
                <span className={`msgr-tcard-due ${due?.tone === 'overdue' ? 'is-overdue' : ''}`}>
                    <FontAwesomeIcon icon={faClock} />
                    {dueText(task)}
                    {due?.tone === 'overdue' && <> · {due.text}</>}
                </span>
            )}

            <span className="msgr-tcard-chips">
                {task.priority && (
                    <span className={`msgr-tchip prio-${slug(task.priority)}`}>{task.priority}</span>
                )}
                <span className={`msgr-tchip status-${slug(task.status)}`}>{task.status}</span>
                {awaiting && <span className="msgr-tchip is-awaiting">Awaiting approval</span>}
                {!task.isMasked && !task.canApprove && <FontAwesomeIcon icon={faChevronRight} className="msgr-tcard-go" />}
            </span>

            <span
                className={`msgr-tcard-bar status-${slug(task.status)}`}
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <span style={{ width: `${progress}%` }} />
            </span>

            {task.canApprove && onApprove && (
                <span className="msgr-tcard-actions">
                    <button
                        type="button"
                        className="is-approve"
                        disabled={busy}
                        onClick={(e) => { e.stopPropagation(); onApprove(task); }}
                    >
                        Approve
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(task); }}>View</button>
                </span>
            )}
        </div>
    );
};

export default ChatTaskCard;
